import { memo, useContext, useState } from 'react';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import AppointmentsRepository from '@/repositories/AppointmentsRepository';
import Button from '@/ui/components/Button';
import Datepicker from '@/ui/components/Form/Datepicker';
import Modal from '@/ui/components/Modal';
import Spinner from '@/ui/components/Spinner';

import type { Props } from './types';

const SendScheduleSummaryButton = ({ personId, size = 'xs', className = 'capitalize' }: Props) => {
  const { addMessage } = useContext(AppContext);
  const apptRepo = AppointmentsRepository();

  const [showModal, setShowModal] = useState(false);
  const [summaryStart, setSummaryStart] = useState<string | null>(
    new Date().toISOString().split('T')[0]
  );
  const [summaryEnd, setSummaryEnd] = useState<string | null>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().split('T')[0];
  });
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    setSummaryError(null);
    if (!summaryStart || !summaryEnd) {
      setSummaryError('Please select both start and end dates.');
      return;
    }
    const start = new Date(summaryStart);
    const end = new Date(summaryEnd);
    if (end < start) {
      setSummaryError('End date must be after start date.');
      return;
    }
    if ((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24) > 35) {
      setSummaryError('Date range cannot exceed 35 days.');
      return;
    }
    setSending(true);
    try {
      const result = await apptRepo.sendScheduleSummaryEmail(personId, summaryStart!, summaryEnd!);
      if (!result.success) {
        setSummaryError(result.message || 'Failed to send email.');
        setSending(false);
        return;
      }
      addMessage({
        level: 'info',
        text: result.message || 'Schedule summary email sent.'
      });
      setShowModal(false);
    } catch (err: unknown) {
      let errorMessage = 'Failed to send email. Please try again.';
      interface ErrorWithMessage {
        message: string;
      }
      function hasMessage(e: unknown): e is ErrorWithMessage {
        return (
          typeof e === 'object' &&
          e !== null &&
          'message' in e &&
          typeof (e as ErrorWithMessage).message === 'string'
        );
      }
      if (hasMessage(err)) {
        errorMessage = err.message;
      }
      setSummaryError(errorMessage);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        className={className}
        size={size}
        color={Color.INDIGO}
        onClick={() => setShowModal(true)}
        dataTestId="send-schedule-summary-email-btn">
        Send Schedule Summary Email
      </Button>

      <Modal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setSummaryError(null);
        }}
        title="Send Schedule Summary Email"
        button={{
          children: sending ? <Spinner /> : 'Send Email',
          color: Color.INDIGO,
          disabled: sending,
          onClick: handleSend
        }}
        dismissButton={{
          children: 'Cancel',
          color: Color.WHITE,
          onClick: () => setShowModal(false)
        }}>
        <div className="flex flex-col gap-4">
          <div className="flex gap-4">
            <Datepicker
              label="Range Start"
              name="summaryStart"
              value={summaryStart}
              onChange={setSummaryStart}
              preventOpenOnFocus
              withPortal
            />
            <Datepicker
              label="Range End"
              name="summaryEnd"
              value={summaryEnd}
              onChange={setSummaryEnd}
              preventOpenOnFocus
              withPortal
            />
          </div>
          <div className="text-xs text-gray-500">Max range: 35 days</div>
          {summaryError && <div className="text-red-500 text-sm">{summaryError}</div>}
        </div>
      </Modal>
    </>
  );
};

export default memo(SendScheduleSummaryButton);
